import {
  Box,
  type BoxProps,
  Button,
  type ButtonProps,
  Grid,
  type GridProps,
  IconButton,
  type IconButtonProps,
  InputBase,
  type InputBaseProps,
  Popper,
  type PopperProps,
  styled,
  TextField,
  type TextFieldProps,
} from '@mui/material';

interface IStyledGrid extends GridProps {
  isVisible: boolean;
}

type IStyledTextField = TextFieldProps & {
  listLength?: number;
};

interface IStyledBoxContainer extends BoxProps {
  showDividers?: boolean;
  validRowHeight?: number;
}

export const StyledGrid = styled(Grid)<IStyledGrid>(({ theme, isVisible }) => ({
  display: isVisible ? 'flex' : 'none',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.lighter,
  overflowX: 'auto',
  whiteSpace: 'nowrap',
}));

export const StyledPopper = styled(Popper)<PopperProps>(({ theme }) => ({
  boxShadow: `0 8px 24px rgba(0, 0, 0, 0.15)`,
  borderRadius: 8,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
  '& ul': {
    padding: 0,
  },
}));

export const BootstrapInput = styled(InputBase)<InputBaseProps>(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.grey[100],
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}));

export const StyledTextField = styled(TextField)<IStyledTextField>(({ theme, listLength }) => ({
  input: {
    '&::placeholder': {
      color: listLength ? '#0B7EB5' : 'inherit',
      opacity: listLength ? 1 : 'inherit',
      fontWeight: 400,
    },
    fontSize: {
      sm: theme.typography.caption,
      md: theme.typography.body2,
      lg: theme.typography.body1,
    },
  },
}));

export const StyledActionBarItem = styled(Button)<ButtonProps>(({ theme }) => ({
  marginLeft: 1,
  '& .MuiListItemIcon-root': {
    minWidth: 'initial',
    marginRight: 0.5,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

export const StyledIconButton = styled(IconButton)<IconButtonProps>(({ theme }) => ({
  position: 'sticky',
  right: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: theme.zIndex.speedDial,
  flexShrink: 0,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.background.paper,
  },
}));

export const StyledBoxContainer = styled(Box)<IStyledBoxContainer>(
  ({ theme, showDividers, validRowHeight }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    ...(showDividers && {
      borderLeft: '1px dashed transparent',
    }),
    ...(validRowHeight && { height: `${validRowHeight}px` }),
  })
);
